import { useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Navigate, useNavigate, useParams, useSearchParams
} from 'react-router-dom';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { gql, useMutation } from '@zenbusiness/application-commons-graphql';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Dialog from '@zenbusiness/zui/Dialog';
import DialogContent from '@zenbusiness/zui/DialogContent';
import IconButton from '@zenbusiness/zui/IconButton';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import WhatsNext, { WHATS_NEXT_STATUS } from '#client/components/WhatsNext';
import { APP_PAGE_NAMES, APP_SECTION } from '#client/constants/clickstream/failedPaymentRecovery';
import { ENABLE_INVOICE_PAYMENT_PAGE } from '#client/constants/experimentsFlags';
import { dashboard, login } from '#client/constants/routes';
import { KlarnaPaymentWidget } from '#client/directEntry/MFEs';
import useUnpaidInvoices from '#client/hooks/useUnpaidInvoices';

import InvoiceCard from './components/InvoiceCard';
import InvoicePaymentsLayout from './components/InvoicePaymentsLayout';
import PaymentOrderInfo from './components/PaymentOrderInfo';
import useBnplRecoveryContext from './hooks/useBnplRecoveryContext';
import usePaymentMethodLink from './hooks/usePaymentMethodLink';
import { InvoicePaymentsEmptyState } from './InvoicePayments.style';

const PAY_BILLING_INVOICE = gql`
  mutation PayBillingInvoice($input: BillingPayInvoiceInput!) {
    payBillingInvoice(input: $input) {
      outcome
      paymentError {
        error
        declineCode
        code
        message
        transactionId
      }
      errorDetails
    }
  }
`;

const MESSAGE_IDS = {
  title: 'dashboard.invoicePayments.title',
  subtitle: 'dashboard.invoicePayments.subtitle',
  noInvoices: 'dashboard.invoicePayments.noInvoices',
  errorTitle: 'dashboard.invoicePayments.error.title',
  errorDescription: 'dashboard.invoicePayments.error.description',
  backToDashboard: 'dashboard.invoicePayments.error.backToDashboard',
  back: 'dashboard.invoicePayments.error.back',
  payWithAnotherMethodError: 'dashboard.invoicePayments.payWithAnotherMethod.error'
};

function InvoicePayments() {
  const { accountUuid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatDate } = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const pageEnabled = useExperimentFlag(ENABLE_INVOICE_PAYMENT_PAGE, null);
  const pageGated = pageEnabled === true;

  const companyName = searchParams.get('companyName') || '';

  const { unpaidInvoices, loading, error } = useUnpaidInvoices(
    pageGated ? accountUuid : null
  );
  const { dynamicProperties } = useBnplRecoveryContext({ unpaidInvoices });
  const [payBillingInvoice] = useMutation(PAY_BILLING_INVOICE, { noThrow: true });
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const sortedInvoices = [...unpaidInvoices].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const firstInvoice = sortedInvoices[0];
  const {
    fetchPaymentMethodLink,
    loading: paymentMethodLinkLoading
  } = usePaymentMethodLink(accountUuid);
  const [paymentMethodLinkError, setPaymentMethodLinkError] = useState(false);
  // useManualQuery's `loading` flips asynchronously on the next render — a synchronous ref
  // guards against double-clicks within the same tick from issuing duplicate requests.
  const paymentMethodLinkInFlightRef = useRef(false);

  const handlePayWithAnotherMethod = async () => {
    if (paymentMethodLinkLoading || paymentMethodLinkInFlightRef.current) return;
    paymentMethodLinkInFlightRef.current = true;
    setPaymentMethodLinkError(false);
    try {
      const url = await fetchPaymentMethodLink();
      if (!url) {
        setPaymentMethodLinkError(true);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      paymentMethodLinkInFlightRef.current = false;
    }
  };

  // Returns null when amount is uncomputable (missing/non-numeric total) so callers can
  // distinguish "we don't know the amount" from "the amount is legitimately $0".
  // The pay button and Dialog are disabled/hidden when this is null to prevent a $0 submission.
  const firstInvoiceAmountInCents = useMemo(() => {
    if (firstInvoice?.total == null) return null;
    const amount = Math.round(Number(firstInvoice.total) * 100);
    return Number.isFinite(amount) ? amount : null;
  }, [firstInvoice?.total]);

  const handlePayInvoice = async ({ confirmationTokenId, threeDSecureResultTokenId }) => {
    // Write line items to sessionStorage at payment initiation — a side effect here is safe
    // (user action, not render) and avoids embedding large JSON in the return_url.
    sessionStorage.setItem(`invoice_lineitems_${firstInvoice?.id}`, JSON.stringify(firstInvoice?.lineItems || []));
    const { data } = await payBillingInvoice({
      variables: {
        input: {
          confirmationTokenId,
          accountUuid,
          invoiceId: firstInvoice?.id,
          amountInCents: firstInvoiceAmountInCents,
          businessEntityName: firstInvoice?.businessEntityName || companyName || undefined,
          returnUrl: '', // required by base schema; not used server-side (embedded in confirmation token)
          ...(threeDSecureResultTokenId && { threeDSecureResultTokenId })
        }
      }
    });
    const result = data?.payBillingInvoice;

    // When Recurly returns a 3DS action token, write it to localStorage here — before returning
    // to the widget — so KlarnaPaymentCheckout can restore the challenge on return even if
    // the Dialog unmounts between the widget's own write and the Klarna redirect.
    // This is a redundant-but-safe write: same key and value the widget writes in submitPayment.
    const actionToken = result?.errorDetails?.three_d_secure_action_token_id;
    if (actionToken && firstInvoice?.id) {
      localStorage.setItem(`klarna_pending_action_token_${firstInvoice.id}`, actionToken);
    }

    return result;
  };

  const backToDashboardButton = (
    <Button
      variant="contained"
      size="large"
      onClick={() => navigate(dashboard)}
      sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
    >
      <FormattedMessage id={MESSAGE_IDS.backToDashboard} />
    </Button>
  );

  const backToInvoicePaymentsButton = (
    <Button
      variant="contained"
      size="large"
      onClick={() => setPaymentMethodLinkError(false)}
      sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
    >
      <FormattedMessage id={MESSAGE_IDS.back} />
    </Button>
  );

  if (pageEnabled === false) {
    return <Navigate to={login} replace />;
  }

  if (!pageGated) {
    return <PageLoading />;
  }

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <InvoicePaymentsLayout
        companyName={companyName}
        titleId={MESSAGE_IDS.errorTitle}
        subtitleId={MESSAGE_IDS.errorDescription}
      >
        <WhatsNext status={WHATS_NEXT_STATUS.UNKNOWN} />
        {backToDashboardButton}
      </InvoicePaymentsLayout>
    );
  }

  if (paymentMethodLinkError) {
    return (
      <InvoicePaymentsLayout
        companyName={companyName}
        titleId={MESSAGE_IDS.errorTitle}
        subtitleId={MESSAGE_IDS.payWithAnotherMethodError}
      >
        <WhatsNext status={WHATS_NEXT_STATUS.UNKNOWN} />
        {backToInvoicePaymentsButton}
      </InvoicePaymentsLayout>
    );
  }

  return (
    <InvoicePaymentsLayout
      companyName={companyName}
      titleId={MESSAGE_IDS.title}
      subtitleId={sortedInvoices.length > 0 ? MESSAGE_IDS.subtitle : undefined}
    >
      {sortedInvoices.length > 0 ? (
        <>
          <PageViewedTracker
            appPageName={APP_PAGE_NAMES.failedPaymentRecovery}
            appSection={APP_SECTION}
            dynamicProperties={dynamicProperties}
          />
          {sortedInvoices.map((invoice, index) => {
            const isPayable = index === 0 && firstInvoiceAmountInCents !== null;
            return (
              <InvoiceCard
                key={invoice.id}
                amount={Number(invoice.total)}
                dueDate={invoice.createdAt ? formatDate(invoice.createdAt, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                dynamicProperties={index === 0 ? dynamicProperties : undefined}
                isLocked={index > 0}
                lineItems={invoice.lineItems || []}
                onPay={isPayable ? () => setShowPaymentModal(true) : undefined}
                onPayWithAnotherMethod={isPayable ? handlePayWithAnotherMethod : undefined}
                payWithAnotherMethodDisabled={index === 0 ? paymentMethodLinkLoading : false}
              />
            );
          })}
          {firstInvoice && firstInvoiceAmountInCents !== null && (
            <Dialog
              fullScreen={fullScreen}
              maxWidth="sm"
              fullWidth
              open={showPaymentModal}
              onClose={() => setShowPaymentModal(false)}
            >
              <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    icon={<CloseIcon />}
                    label="Close"
                    onClick={() => setShowPaymentModal(false)}
                  />
                </Box>
                <KlarnaPaymentWidget
                  accountUuid={accountUuid}
                  amountInCents={firstInvoiceAmountInCents}
                  companyName={companyName}
                  invoiceNumber={firstInvoice.id}
                  lineItems={firstInvoice.lineItems || []}
                  unpaidCount={Math.max(0, unpaidInvoices.length - 1)}
                  onPayInvoice={handlePayInvoice}
                />
              </DialogContent>
            </Dialog>
          )}
          <PaymentOrderInfo />
        </>
      ) : (
        <InvoicePaymentsEmptyState>
          <FormattedMessage id={MESSAGE_IDS.noInvoices} />
        </InvoicePaymentsEmptyState>
      )}
    </InvoicePaymentsLayout>
  );
}

export default InvoicePayments;
